import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';

import {omit} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useCreateVehicleMutation, useGetVehicleQuery} from '@dms/api/vehicle';
import {useCreateServiceVehicleMutation} from '@dms/api/workshop';
import {
  SERVICE_INTERVALS_DEFAULT_VALUES,
  ServiceVehicle,
  ServiceVehicleValidationSchema,
  toAPIVehicle,
  toBaseVehicle,
  useAdditionalNote,
  useFormNavigate,
  useGetCarFeatures,
  useVehicleDocumentSync,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {formatDocumentTitle, handleApiError, useCloneCustomFields} from '@dms/shared';

import {noop, useRequiredParams} from 'shared/web';

import {ServiceVehicleForm} from '../components/ServiceVehicleForm';

export function CloneServiceVehicle() {
  const {navigateOnDiscard, navigateOnConfirm} = useFormNavigate();
  const {id: vehicleId} = useRequiredParams();

  const [
    cloneCustomFields,
    {isLoading: isLoadingVehicleCustomFields, isError: isErrorVehicleCustomFields},
  ] = useCloneCustomFields(vehicleId);

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery({vehicleId}, {skip: isNilOrEmpty(vehicleId)});

  const [createVehicleMutation, {isLoading: isCreatingVehicle}] = useCreateVehicleMutation();
  const [createServiceVehicle, {isLoading: isServiceVehicleCreating}] =
    useCreateServiceVehicleMutation();

  const {handleSaveNote} = useAdditionalNote();
  const [getCarFeatures] = useGetCarFeatures();
  const {syncDocuments} = useVehicleDocumentSync();

  // const {
  //   generateSchema,
  //   isLoading: isMandatoryFieldsLoading,
  //   isMandatoryFieldsEnabled,
  // } = useMandatoryFields();

  // const [formSchema, setFormSchema] = useState<ZodSchema<ServiceVehicle>>(() =>
  //   generateSchema(ServiceVehicleValidationSchema, 'VEHICLETYPE_PASSENGER_CAR')
  // );

  // const handleVehicleTypeChange = useCallback(
  //   (type: VehicleTypeEnum) => {
  //     const newSchema = generateSchema(ServiceVehicleValidationSchema, type);
  //     setFormSchema(newSchema);
  //   },
  //   [generateSchema]
  // );

  const handleSave = async (values: ServiceVehicle) => {
    const sourcingData = await getCarFeatures(values);
    const existingFeatures = vehicle?.features || null;
    const apiPayload = toAPIVehicle(values, existingFeatures, sourcingData);

    await createVehicleMutation({
      createVehicleRequestBody: apiPayload,
      validateMandatoryFields: false,
    })
      .unwrap()
      // Handle service vehicle creation
      .then(async (vehicle) => {
        await createServiceVehicle({
          vehicleId: vehicle.id,
        }).unwrap();
        return vehicle;
      })
      // Handle additional note
      .then(async (vehicle) => {
        if (values.additionalNote) {
          await handleSaveNote(vehicle.id, values.additionalNote);
        }
        return vehicle.id;
      })
      // Sync battery certificate document
      .then(async (vehicleId) => {
        await syncDocuments({
          vehicleId,
          contextTarget: 'vehicle',
          fileFields: [
            {
              fieldValue: values.batteryCertificateDocument,
              documentNote: i18n.t('entity.vehicle.labels.batteryCertificate'),
            },
          ],
        });
        return vehicleId;
      })
      // Clone custom fields
      .then(cloneCustomFields)
      .then((vehicleId) => {
        navigateOnConfirm(vehicleId);
      })
      .catch(handleApiError);
  };

  const isCreating = isCreatingVehicle || isServiceVehicleCreating;
  const isLoading =
    /* isMandatoryFieldsLoading || */ isVehicleLoading || isLoadingVehicleCustomFields;
  const isError = isVehicleError || isErrorVehicleCustomFields;

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.labels.cloneServiceVehicle'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus isLoading={isLoading} isError={isError}>
        <VFKit.Form
          experimentalZodSchema={ServiceVehicleValidationSchema}
          defaultValues={{
            ...toBaseVehicle(omit(['registrationPlate', 'vin'], vehicle!)),
            serviceIntervals: {
              ...SERVICE_INTERVALS_DEFAULT_VALUES,
              ...vehicle?.serviceIntervals,
            },
          }}
        >
          <ServiceVehicleForm
            onSave={handleSave}
            onDiscard={navigateOnDiscard}
            isCreating={isCreating}
            onVehicleTypeChange={noop}
            tabRoutePattern="cloneServiceVehicleTabV2"
            modalKeys={{
              headline: i18n.t('entity.vehicle.labels.cloneServiceVehicle'),
              create: i18n.t('entity.vehicle.labels.clone'),
            }}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
